// Cosmetic shop modal — spends 💎 crystals from the persistent meta
// wallet. Owned items show EQUIP / EQUIPPED / UNEQUIP controls.
// 2026-04-28 — unified to a single currency; the old "special points"
// flow folded into crystals. Item prices already live in
// `cosmetics.ts:price` and were re-costed in the same pass.

import {
  getCrystals,
  spendCrystals,
  grantItem,
  ownsItem,
  equip,
  unequip,
  isEquipped,
} from '../meta/wallet';
import { COSMETICS } from '../meta/cosmetics';

export function openCosmeticShop(onClose?: () => void): () => void {
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed; inset: 0;
    background: rgba(2,6,23,.93);
    z-index: 9998;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    color: #e5e7eb; font: 700 14px system-ui, sans-serif;
    padding: 20px;
  `;

  const title = document.createElement('div');
  title.style.cssText = `
    font: 900 28px system-ui; letter-spacing: 4px; margin-bottom: 6px;
    background: linear-gradient(90deg,#f472b6,#fbbf24);
    -webkit-background-clip: text; background-clip: text; color: transparent;
  `;
  title.textContent = '💎 COSMETICS';
  overlay.appendChild(title);

  const sub = document.createElement('div');
  sub.style.cssText = `opacity:.65;font-size:11px;letter-spacing:2.5px;margin-bottom:20px;`;
  sub.textContent = 'SHOP · PULLED FROM GACHA · EQUIP YOUR LOOK';
  overlay.appendChild(sub);

  const grid = document.createElement('div');
  grid.style.cssText = `
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 14px;
    width: 100%;
    max-width: 800px;
    max-height: 62vh;
    overflow-y: auto;
    padding: 6px;
  `;
  overlay.appendChild(grid);

  const wallet = document.createElement('div');
  wallet.style.cssText = `
    margin-top: 20px;
    padding: 6px 14px;
    background: rgba(15,23,42,.65);
    border: 1px solid #475569;
    border-radius: 8px;
    font: 800 12px system-ui;
    letter-spacing: 2px;
    color: #fde047;
  `;
  overlay.appendChild(wallet);

  const toast = document.createElement('div');
  toast.style.cssText = `
    margin-top: 10px;
    min-height: 16px;
    font: 700 11px system-ui;
    letter-spacing: 1.5px;
    opacity: 0;
    transition: opacity .2s;
  `;
  overlay.appendChild(toast);
  let toastTimer = 0;
  function flashToast(msg: string, color: string) {
    toast.textContent = msg;
    toast.style.color = color;
    toast.style.opacity = '1';
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => { toast.style.opacity = '0'; }, 1600);
  }

  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'CLOSE [ESC]';
  closeBtn.style.cssText = `
    margin-top: 10px;
    padding: 8px 22px;
    border-radius: 8px;
    border: 1px solid #475569;
    background: rgba(15,23,42,.85);
    color: #e5e7eb;
    cursor: pointer;
    font: 700 12px system-ui;
    letter-spacing: 1.5px;
  `;
  overlay.appendChild(closeBtn);

  function render() {
    const bal = getCrystals();
    wallet.textContent = `💎 CRYSTALS: ${bal.toLocaleString()}`;
    grid.innerHTML = COSMETICS.map((c) => {
      const owned = ownsItem(c.id);
      const equipped = isEquipped(c.id);
      const price = c.price ?? 0;
      const canAfford = bal >= price;
      let buttonHtml: string;
      if (owned && equipped) {
        buttonHtml = `<button data-item="${c.id}" data-action="unequip" style="
          margin-top: 10px; padding: 6px 14px;
          background: #1f2937; color: #fde047;
          border: 1px solid #fde047; border-radius: 8px;
          font: 800 11px system-ui; cursor: pointer;
          letter-spacing: .5px;
        ">✓ EQUIPPED</button>`;
      } else if (owned) {
        buttonHtml = `<button data-item="${c.id}" data-action="equip" style="
          margin-top: 10px; padding: 6px 14px;
          background: ${c.color}; color: #0f172a;
          border: 0; border-radius: 8px;
          font: 800 11px system-ui; cursor: pointer;
          box-shadow: 0 4px 14px ${c.color}66;
          letter-spacing: .5px;
        ">EQUIP</button>`;
      } else if (c.source === 'gacha') {
        buttonHtml = `<button data-item="${c.id}" data-action="gacha" style="
          margin-top: 10px; padding: 6px 14px;
          background: rgba(15,23,42,.8); color: #fde047;
          border: 1px solid #f59e0b; border-radius: 8px;
          font: 800 11px system-ui; cursor: help;
          letter-spacing: .5px;
        ">💎 GACHA</button>`;
      } else if (!canAfford) {
        buttonHtml = `<button data-item="${c.id}" data-action="cant" style="
          margin-top: 10px; padding: 6px 14px;
          background: rgba(15,23,42,.8); color: #64748b;
          border: 1px solid #475569; border-radius: 8px;
          font: 800 11px system-ui; cursor: not-allowed;
          letter-spacing: .5px;
        ">💎 ${price}</button>`;
      } else {
        buttonHtml = `<button data-item="${c.id}" data-action="buy" style="
          margin-top: 10px; padding: 6px 14px;
          background: ${c.color}; color: #0f172a;
          border: 0; border-radius: 8px;
          font: 800 11px system-ui; cursor: pointer;
          box-shadow: 0 4px 14px ${c.color}66;
          letter-spacing: .5px;
        ">💎 ${price}</button>`;
      }
      const rarityTag = c.rarity
        ? `<div style="font:700 9px system-ui;color:${c.color};letter-spacing:1.5px;margin-top:2px;">${c.rarity.toUpperCase()}</div>`
        : '';
      return `
        <div style="
          background: linear-gradient(135deg,${c.color}22,rgba(0,0,0,.72));
          border: 1px solid ${c.color};
          border-radius: 12px;
          padding: 14px 10px;
          text-align: center;
          box-shadow: 0 6px 20px rgba(0,0,0,.5);
          ${equipped ? 'box-shadow: 0 0 24px #fde04766, 0 6px 20px rgba(0,0,0,.5);' : ''}
          ${!owned && c.source === 'gacha' ? 'filter: grayscale(.35); opacity:.78;' : ''}
        ">
          <div style="font-size:40px;margin-bottom:6px;filter:drop-shadow(0 0 12px ${c.color});">${c.icon}</div>
          <div style="font:800 12px system-ui;letter-spacing:.5px;">${c.name}</div>
          <div style="font:600 9px system-ui;opacity:.6;text-transform:uppercase;margin-top:2px;letter-spacing:1.5px;">${c.slot}</div>
          ${rarityTag}
          ${buttonHtml}
        </div>
      `;
    }).join('');
    grid.querySelectorAll<HTMLButtonElement>('button[data-action="buy"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.item!;
        const item = COSMETICS.find((c) => c.id === id);
        if (!item || item.price == null) return;
        if (!spendCrystals(item.price)) {
          flashToast('NOT ENOUGH CRYSTALS', '#ef4444');
          return;
        }
        grantItem(id);
        flashToast(`✓ UNLOCKED ${item.name.toUpperCase()}`, '#86efac');
        render();
      });
    });
    grid.querySelectorAll<HTMLButtonElement>('button[data-action="equip"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.item!;
        equip(id);
        flashToast(`EQUIPPED`, '#fde047');
        render();
      });
    });
    grid.querySelectorAll<HTMLButtonElement>('button[data-action="unequip"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.item!;
        const item = COSMETICS.find((c) => c.id === id);
        if (!item) return;
        unequip(item.slot);
        flashToast(`UNEQUIPPED`, '#94a3b8');
        render();
      });
    });
    grid.querySelectorAll<HTMLButtonElement>('button[data-action="cant"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        flashToast('NOT ENOUGH SPECIAL POINTS', '#ef4444');
      });
    });
    grid.querySelectorAll<HTMLButtonElement>('button[data-action="gacha"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        flashToast('PULL FROM THE GACHA VENDOR', '#fde047');
      });
    });
  }
  render();

  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    removeEventListener('keydown', onKey);
    clearTimeout(toastTimer);
    overlay.remove();
    onClose?.();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.code === 'Escape') close();
  };
  addEventListener('keydown', onKey);
  closeBtn.addEventListener('click', close);

  document.body.appendChild(overlay);
  return close;
}
